import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { applyDocumentTitle, createI18n } from '@barahpay/shared';
import { PaymentPage } from './pages/PaymentPage';
import { ResultPage } from './pages/ResultPage';
import { NotAvailablePage } from './pages/NotAvailablePage';
import { setApiLanguage } from './api';
// The brand stylesheet carries the self-hosted fonts and the palette, so it loads before the page rules.
import '@barahpay/shared/brand.css';
import './styles.css';

// The browser's preference decides the initial language; the payer can switch it on the page
// (spec section 87).
const preferred = navigator.language?.startsWith('ar') ? 'ar' : 'en';
const i18n = createI18n(preferred);
applyDocumentTitle(i18n.t('common.appName'));
setApiLanguage(preferred);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A payment page must not serve stale state, and a payer should not have to reload.
      staleTime: 0,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* The public URL shape from spec section 18: pay.barah.sa/p/{token} */}
            <Route path="/p/:token" element={<PaymentPage />} />
            <Route path="/p/:token/result" element={<ResultPage />} />
            <Route path="/unavailable" element={<NotAvailablePage />} />
            <Route path="*" element={<Navigate to="/unavailable" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
